import { useState } from 'react';
import { useGameProfileVerificationStart } from '../hooks/useGameProfileVerificationStart.js';

export function MobileGameProfileVerification() {
  const verification = useGameProfileVerificationStart();
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    if (!verification.challenge?.verificationCode) return;

    try {
      await navigator.clipboard.writeText(verification.challenge.verificationCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const resetOrReissue = () => {
    if (verification.expired && verification.hasSavedProfile) {
      verification.reverifyCurrentProfile();
      return;
    }
    verification.resetChallenge();
  };

  return (
    <section className="mobile-game-profile-panel" aria-label="BlablaLink 게임 프로필 인증">
      <div className="mobile-game-profile-heading">
        <div>
          <p className="section-eyebrow">BLABLALINK</p>
          <h2>게임 프로필 연결</h2>
        </div>
        <span>03</span>
      </div>

      <p className="mobile-game-profile-description">
        프로필 URL과 일회용 상태 메시지 코드로 본인 게임 계정을 확인합니다.
      </p>

      {verification.profileLoading ? (
        <p className="mobile-game-profile-notice checking" role="status">
          연결된 게임 프로필을 확인하고 있습니다…
        </p>
      ) : verification.verifiedProfile ? (
        <div className="mobile-game-profile-verified" role="status">
          <div className="mobile-game-profile-verified-mark" aria-hidden="true">✓</div>
          <div>
            <span>BLABLALINK VERIFIED</span>
            <h3>{verification.verifiedProfile.nickname || '닉네임 확인 완료'}</h3>
            <p>유니온: {verification.verifiedProfile.guildName || '미가입 또는 확인되지 않음'}</p>
            <strong>게임 프로필 연결 완료</strong>
            {verification.checkNotice.message ? (
              <p className={`mobile-game-profile-notice ${verification.checkNotice.type || 'success'}`}>
                {verification.checkNotice.message}
              </p>
            ) : null}
            <button
              className="mobile-game-profile-check"
              type="button"
              onClick={verification.reverifyCurrentProfile}
              disabled={verification.submitting}
            >
              {verification.submitting ? '재인증 코드 발급 중…' : '저장된 BlablaLink로 다시 인증'}
            </button>
          </div>
        </div>
      ) : !verification.challenge ? (
        <form className="mobile-game-profile-form" onSubmit={verification.submit}>
          <label>
            <span>프로필 URL</span>
            <input
              type="url"
              value={verification.profileUrl}
              onChange={(event) => verification.setProfileUrl(event.target.value)}
              placeholder="https://www.blablalink.com/user?openid=..."
              autoComplete="url"
              inputMode="url"
              disabled={verification.submitting}
              required
            />
          </label>

          {verification.notice.message ? (
            <p className={`mobile-game-profile-notice ${verification.notice.type}`} role="status">
              {verification.notice.message}
            </p>
          ) : null}

          <button type="submit" disabled={verification.submitting}>
            {verification.submitting ? '코드 발급 중…' : '인증 코드 발급'}
          </button>
        </form>
      ) : (
        <div className="mobile-game-profile-challenge">
          <div className="mobile-game-profile-code-block">
            <span>인증 코드</span>
            <strong>{verification.challenge.verificationCode}</strong>
            <button type="button" onClick={copyCode}>{copied ? '복사됨' : '복사'}</button>
          </div>

          <div className={`mobile-game-profile-timer ${verification.expired ? 'expired' : ''}`}>
            <span>{verification.expired ? '코드 만료' : '코드 남은 시간'}</span>
            <strong>{verification.formattedRemaining}</strong>
          </div>

          <ol className="mobile-game-profile-steps">
            <li>{verification.hasSavedProfile ? '기존 BlablaLink 프로필' : 'BlablaLink 프로필'} 상태 메시지 편집</li>
            <li>인증 코드를 그대로 입력하고 저장</li>
            <li>아래 버튼을 한 번 눌러 자동 확인 시작</li>
          </ol>

          {verification.checking ? (
            <div className="mobile-game-profile-check-progress" role="status" aria-live="polite">
              <div className="mobile-game-profile-check-title">
                <span className="mobile-game-profile-spinner" aria-hidden="true" />
                <strong>BlablaLink 불러오는 중</strong>
              </div>
              <p>{verification.checkNotice.message}</p>
              <div className="mobile-game-profile-check-meta">
                <span>{verification.checkAttemptCount ? `${verification.checkAttemptCount}회 확인` : '첫 확인 중'}</span>
                <strong>{verification.formattedCheckRemaining}</strong>
              </div>
            </div>
          ) : verification.checkNotice.message ? (
            <p className={`mobile-game-profile-notice ${verification.checkNotice.type}`} role="status">
              {verification.checkNotice.message}
            </p>
          ) : null}

          <div className="mobile-game-profile-actions">
            <button
              className="mobile-game-profile-check"
              type="button"
              onClick={verification.checking ? verification.cancelChecking : verification.startChecking}
              disabled={verification.expired}
            >
              {verification.checking ? '자동 확인 중지' : '상태 메시지 저장 완료 · 인증 시작'}
            </button>
            <button
              className="mobile-game-profile-reissue"
              type="button"
              onClick={resetOrReissue}
              disabled={verification.checking || verification.submitting}
            >
              {verification.expired
                ? (verification.hasSavedProfile ? '저장된 프로필로 새 코드 발급' : '새 코드 발급하기')
                : '인증 취소'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
